import React, { useEffect, useState } from "react";
import { getArticleCount, getUserCount, getAuditStats, getRecentAuditLogs } from "./_reques";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import PeopleIcon from '@mui/icons-material/People';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import BookIcon from '@mui/icons-material/Book';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import CollectionsIcon from '@mui/icons-material/Collections';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

export default function Dashboard() {
  const { authToken, userData, themeMode } = useAuth();
  const isDark = themeMode === 'dark';
  const navigate = useNavigate();

  const [articleCount, setArticleCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [auditStats, setAuditStats] = useState({
    totalLogs: 0,
    actionsToday: 0,
    activeUsersToday: 0,
    topModule: 'N/A'
  });
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const [articleRes, userRes, auditRes, recentRes] = await Promise.all([
          getArticleCount(authToken),
          getUserCount(authToken),
          getAuditStats(authToken),
          getRecentAuditLogs(authToken)
        ]);

        if (articleRes?.Result) setArticleCount(articleRes.ArticleCount || 0);
        if (userRes?.Result) setUserCount(userRes.UserCount || 0);
        if (auditRes?.Result && auditRes.stats) setAuditStats(auditRes.stats);
        if (recentRes?.Result && Array.isArray(recentRes.data)) setRecentLogs(recentRes.data);
      } catch (error) {
        console.error('Error fetching dashboard metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [authToken]);

  const cards = [
    {
      id: 1,
      title: "Total System Users",
      number: userCount,
      trend: "Active Users",
      icon: <PeopleIcon className="text-indigo-400" fontSize="large" />,
      gradient: isDark ? "from-indigo-500/20 to-purple-500/10" : "from-indigo-500/10 to-purple-500/5",
      borderColor: isDark ? "border-indigo-500/30" : "border-indigo-500/20",
    },
    {
      id: 2,
      title: "Tazakuraat Articles",
      number: articleCount,
      trend: "Published Articles",
      icon: <MenuBookIcon className="text-cyan-400" fontSize="large" />,
      gradient: isDark ? "from-cyan-500/20 to-blue-500/10" : "from-cyan-500/10 to-blue-500/5",
      borderColor: isDark ? "border-cyan-500/30" : "border-cyan-500/20",
    },
    {
      id: 3,
      title: "Total Matbuaat & Books",
      number: 24,
      trend: "Library Catalog",
      icon: <BookIcon className="text-emerald-400" fontSize="large" />,
      gradient: isDark ? "from-emerald-500/20 to-teal-500/10" : "from-emerald-500/10 to-teal-500/5",
      borderColor: isDark ? "border-emerald-500/30" : "border-emerald-500/20",
    },
    {
      id: 4,
      title: "Actions Recorded Today",
      number: auditStats.actionsToday,
      trend: "System Activity",
      icon: <HistoryIcon className="text-rose-400" fontSize="large" />,
      gradient: isDark ? "from-rose-500/20 to-red-500/10" : "from-rose-500/10 to-red-500/5",
      borderColor: isDark ? "border-rose-500/30" : "border-rose-500/20",
    },
    {
      id: 5,
      title: "Active Admins Today",
      number: auditStats.activeUsersToday,
      trend: "Logged-in Staff",
      icon: <PersonIcon className="text-amber-400" fontSize="large" />,
      gradient: isDark ? "from-amber-500/20 to-yellow-500/10" : "from-amber-500/10 to-yellow-500/5",
      borderColor: isDark ? "border-amber-500/30" : "border-amber-500/20",
    },
    {
      id: 6,
      title: "Class Form Submissions",
      number: 8,
      trend: "New Form Entries",
      icon: <AssignmentIcon className="text-blue-400" fontSize="large" />,
      gradient: isDark ? "from-blue-500/20 to-indigo-500/10" : "from-blue-500/10 to-indigo-500/5",
      borderColor: isDark ? "border-blue-500/30" : "border-blue-500/20",
    },
    {
      id: 7,
      title: "Mujjalah Issues",
      number: 15,
      trend: "Published Editions",
      icon: <CollectionsIcon className="text-purple-400" fontSize="large" />,
      gradient: isDark ? "from-purple-500/20 to-pink-500/10" : "from-purple-500/10 to-pink-500/5",
      borderColor: isDark ? "border-purple-500/30" : "border-purple-500/20",
    },
    {
      id: 8,
      title: "Total System Logs",
      number: auditStats.totalLogs,
      trend: "Audit History",
      icon: <ShieldIcon className="text-sky-400" fontSize="large" />,
      gradient: isDark ? "from-sky-500/20 to-cyan-500/10" : "from-sky-500/10 to-cyan-500/5",
      borderColor: isDark ? "border-sky-500/30" : "border-sky-500/20",
    },
  ];

  const quickActions = [
    { title: "Add New Article", path: "/tazakuraat/addTazakraat" },
    { title: "Manage Users", path: "/users" },
    { title: "Upload Noor Diary", path: "/noor-diary/createNoorDiary" },
    { title: "View Audit Logs", path: "/auditlogs" },
  ];

  return (
    <div className="space-y-8 pb-10">
      {/* Welcome Banner */}
      <div className="glass-panel p-6 sm:p-8 relative overflow-hidden flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="relative z-10 space-y-2 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
            <ShieldIcon fontSize="small" /> System Dashboard Overview
          </div>
          <h1 className={`text-3xl sm:text-4xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Welcome back, <span className="gradient-text">{userData?.email || "Admin"}</span>
          </h1>
          <p className={`text-sm sm:text-base leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            Manage Mushtaqan e Noor content, view real-time activity metrics, user management, and administrative logs.
          </p>
        </div>

        <div className="relative z-10 flex flex-wrap gap-3">
          <button 
            onClick={() => navigate('/tazakuraat/addTazakraat')}
            className="glass-btn-primary px-5 py-3 flex items-center gap-2 text-sm shadow-lg"
          >
            <AddCircleOutlineIcon fontSize="small" /> New Article
          </button>
          <button 
            onClick={() => navigate('/auditlogs')}
            className="glass-btn-secondary px-5 py-3 flex items-center gap-2 text-sm"
          >
            <HistoryIcon fontSize="small" /> Activity Logs
          </button>
        </div>

        <div className="absolute -right-10 -bottom-10 w-64 h-64 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />
      </div>

      {/* 8 Glassmorphic Metric Cards Grid */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
            <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse" />
            Key System Metrics
          </h2>
          <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Live updated stats</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {cards.map((card) => (
            <div 
              key={card.id} 
              className={`glass-card p-5 relative overflow-hidden bg-gradient-to-br ${card.gradient} border ${card.borderColor}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {card.title}
                  </span>
                  <div className={`text-3xl font-extrabold mt-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {loading ? "..." : card.number}
                  </div>
                </div>
                <div className={`p-2.5 rounded-xl backdrop-blur-md shadow-inner border ${
                  isDark ? 'bg-white/10 border-white/15' : 'bg-slate-200/60 border-slate-300'
                }`}>
                  {card.icon}
                </div>
              </div>

              <div className={`mt-4 pt-3 border-t flex items-center justify-between text-xs ${
                isDark ? 'border-white/10' : 'border-slate-200'
              }`}>
                <span className={`font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{card.trend}</span>
                <span className="text-cyan-500 font-semibold flex items-center gap-1">
                  Active <CheckCircleOutlineIcon style={{ fontSize: 12 }} />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions & Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions Panel */}
        <div className="glass-panel p-6 space-y-4">
          <h3 className={`text-base font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <AddCircleOutlineIcon className="text-indigo-400" />
            Quick Administration
          </h3>
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Frequently used actions for content creation and staff management.
          </p>

          <div className="grid grid-cols-1 gap-3 pt-1">
            {quickActions.map((act, i) => (
              <button
                key={i}
                onClick={() => navigate(act.path)}
                className={`w-full glass-card p-4 flex items-center justify-between text-left group hover:border-indigo-500/50 ${
                  isDark ? 'bg-slate-900/60' : 'bg-white'
                }`}
              >
                <span className={`text-sm font-semibold transition-colors ${
                  isDark ? 'text-slate-200 group-hover:text-white' : 'text-slate-700 group-hover:text-slate-900'
                }`}>
                  {act.title}
                </span>
                <ArrowForwardIcon className="text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" fontSize="small" />
              </button>
            ))}
          </div>
        </div>

        {/* Recent Audit Activity Feed (2 Cols) */}
        <div className="glass-panel p-6 space-y-4 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className={`text-base font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <HistoryIcon className="text-rose-400" />
              Recent System Activity Feed
            </h3>
            <button 
              onClick={() => navigate('/auditlogs')}
              className="text-xs font-semibold text-cyan-500 hover:underline flex items-center gap-1"
            >
              View All Logs <ArrowForwardIcon style={{ fontSize: 12 }} />
            </button>
          </div>

          <div className="space-y-3">
            {recentLogs.length > 0 ? (
              recentLogs.map((log) => (
                <div key={log.id} className={`p-3.5 rounded-xl border flex items-center justify-between gap-4 text-xs ${
                  isDark ? 'bg-slate-900/60 border-white/5' : 'bg-white border-slate-200 shadow-sm'
                }`}>
                  <div className="flex items-center space-x-3 overflow-hidden">
                    <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 font-bold text-[10px]">
                      {log.module}
                    </div>
                    <div className="overflow-hidden">
                      <div className={`font-semibold truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                        {log.user_email || 'System'}
                      </div>
                      <div className={`truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {log.action}
                      </div>
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      log.status === 'SUCCESS' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                    }`}>
                      {log.status}
                    </span>
                    <div className={`text-[10px] mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      {new Date(log.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className={`text-center py-8 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                No recent activity logged.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
